import { hasDeviceExternalUrl } from "../deviceConnectionUtils";
import { keyboardVidPidNeedsProductNameSplit } from "../keyboardDeviceMatch";
import { isGenericHidKeyboardProductName } from "../genericKeyboardOtherOnly";

const normProductName = (s: unknown) =>
  typeof s === "string" ? s.trim().toLowerCase() : "";

/** 智慧无限方案默认 HID 通信接口（实机/DK87：usagePage=65415/0xFF87，usage=32/0x20） */
export const ZHWX_DEFAULT_USAGE_PAGE = 0xff87;
export const ZHWX_DEFAULT_USAGE = 0x20;

/** 智慧无限 2.4G 无线连接（接收器 Dongle） */
export function isZhwxWirelessConfiguration(configuration: any): boolean {
  if (!isZhwxKeyboard(configuration)) return false;
  return (
    configuration.is4G === true ||
    configuration.workMode === 2 ||
    configuration.workMode === "2"
  );
}

export type ZhwxBatteryStatus = {
  level: number;
  charging: boolean;
};

/** 电量字节：公版多为 BCD（如 0x75→75%），否则按 0~100 直出 */
function decodeZhwxBatteryLevelByte(value: number): number {
  const hi = (value >> 4) & 0x0f;
  const lo = value & 0x0f;
  if (hi <= 9 && lo <= 9) {
    return Math.min(100, hi * 10 + lo);
  }
  return Math.min(100, Math.max(0, value));
}

/** 解析 0x1E 读电量响应 payload（byte8 起，部分固件仍走此路径） */
export function parseZhwxBatteryPayload(
  payload: Uint8Array
): ZhwxBatteryStatus {
  const level = decodeZhwxBatteryLevelByte(payload[0] ?? 0);
  const charging = (payload[1] ?? 0) !== 0;
  return { level, charging };
}

/**
 * 解析读电量 64B 应答（按 command 区分布局）：
 * - 0x1E（length=2）：byte5=电量(BCD)，byte6=充电，byte7=0x55
 * - 0x1F（length=0x18，公版页面轮询）：byte7=0x55，byte8=电量，byte9=充电
 */
export function parseZhwxBatteryResponse(
  response: Uint8Array
): ZhwxBatteryStatus {
  const command = response[1] ?? 0;
  const resultCode = response[7] ?? 0;

  if (command === 0x1f && response.length >= 10 && resultCode === 0x55) {
    const level = decodeZhwxBatteryLevelByte(response[8] ?? 0);
    const charging = (response[9] ?? 0) !== 0;
    if (level > 0 || charging) {
      return { level, charging };
    }
  }

  const dataLen = response[4] ?? 0;
  if (
    command === 0x1e &&
    response.length >= 8 &&
    dataLen >= 1 &&
    (resultCode === 0x55 || resultCode === 0)
  ) {
    const headerLevel = decodeZhwxBatteryLevelByte(response[5] ?? 0);
    const headerCharging = (response[6] ?? 0) !== 0;
    if (headerLevel > 0 || headerCharging) {
      return { level: headerLevel, charging: headerCharging };
    }
  }

  return parseZhwxBatteryPayload(response.slice(8));
}

/** 设备卡片 / usePowerLevel 统一电量结构 */
export function formatZhwxBatteryPower(
  battery: ZhwxBatteryStatus,
  workMode: number = 2
): {
  level: number;
  charging: boolean;
  full: boolean;
  workMode: number;
} {
  return {
    level: battery.level,
    charging: battery.charging,
    full: battery.level >= 100 && !battery.charging,
    workMode,
  };
}

/** 是否为智慧无限驱动内键盘方案 */
export function isZhwxKeyboard(configuration: any): boolean {
  if (!configuration || configuration.solutionRovider !== "ZHWX") {
    return false;
  }
  if (hasDeviceExternalUrl(configuration)) return false;
  return true;
}

function collectionEquals(
  collection: any,
  usagePage: number,
  usage: number
): boolean {
  return collection?.usagePage === usagePage && collection?.usage === usage;
}

/** 智慧无限 HID collection 是否与配置表 usagePage/usage 一致 */
export function zhwxHidCollectionMatches(
  configuration: any,
  collection: any
): boolean {
  if (!isZhwxKeyboard(configuration)) {
    return false;
  }
  const usagePage =
    typeof configuration.usagePage === "number"
      ? configuration.usagePage
      : ZHWX_DEFAULT_USAGE_PAGE;
  const usage =
    typeof configuration.usage === "number"
      ? configuration.usage
      : ZHWX_DEFAULT_USAGE;
  return collectionEquals(collection, usagePage, usage);
}

/** info_tab byte10~11 是否尚未写入有效版本 */
export function isZhwxFirmwareVersionUnset(version: number): boolean {
  const raw = Math.max(0, Math.trunc(Number(version) || 0));
  return raw === 0;
}

/**
 * info_tab 生产版本（byte10~11，小端 uint16：高字节主版本、低字节次版本）→ 公版展示串
 * 公版 Hub：两字节均为 0 时默认展示 1.0；有效版本为 major.minor（无前导零），如 1.0、1.3
 * （UI 层会再拼接前缀 V，勿在此重复加 V）
 */
export function formatZhwxFirmwareVersion(version: number): string {
  const raw = Math.max(0, Math.trunc(Number(version) || 0));
  if (isZhwxFirmwareVersionUnset(raw)) {
    return "1.0";
  }
  const major = (raw >> 8) & 0xff;
  const minor = raw & 0xff;
  return `${major}.${minor}`;
}

/** 智慧无限 HID 是否与配置表行对应 */
export function zhwxHidMatchesConfiguration(
  configuration: any,
  device: any
): boolean {
  if (!isZhwxKeyboard(configuration)) {
    return false;
  }
  if (
    device?.vendorId !== configuration.vendorId ||
    device?.productId !== configuration.productId
  ) {
    return false;
  }
  const collections = device?.collections ?? [];
  let hasMatchingCollection = collections.some((collection: any) =>
    zhwxHidCollectionMatches(configuration, collection)
  );
  // 实机 usage 未在已知列表时，回退到任意厂商自定义接口（0xFF00+）
  if (!hasMatchingCollection) {
    hasMatchingCollection = collections.some(
      (collection: any) =>
        typeof collection?.usagePage === "number" &&
        collection.usagePage >= 0xff00
    );
  }
  if (!hasMatchingCollection) {
    return false;
  }
  if (isGenericHidKeyboardProductName(device?.productName)) {
    return true;
  }
  const cfgPn = normProductName(configuration.productName);
  const devPn = normProductName(device?.productName);
  if (
    keyboardVidPidNeedsProductNameSplit(
      configuration.vendorId,
      configuration.productId
    )
  ) {
    return cfgPn !== "" && cfgPn === devPn;
  }
  return true;
}
